import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { TableCard } from '@tempmee/react-blocks/experimental';

const COLUMNS = ['Name', 'Email', 'Role'];

/**
 * The GoTu Admin list, framed by `TableCard`, which owns the loading and empty
 * states so the frame stays put between pages.
 *
 * `title={null}` is product's call: the page header already reads "GoTu Admin",
 * so a second heading directly beneath it is noise. It is passed rather than
 * omitted because TableCard types `title` as required — and its header Box
 * (`px-4 pt-4 pb-3`) renders unconditionally, so this removes the text but
 * leaves ~28px of empty band above the table. Composing the card by hand
 * removes the band too, at the cost of duplicating the block; we chose the band.
 * The real fix is making `title` optional in @tempmee/react-blocks so the header
 * Box is skipped when absent, which would let this become a plain omission.
 *
 * The error branch is ours: TableCard has no failure state, and "no admins
 * match" must not stand in for a request that failed — the two look identical
 * to an admin narrowing a filter, and only one of them means "try again".
 *
 * Read-only by design (GOTU-5861). Roles are sourced from Google Workspace and
 * auth-service does not register its PATCH route in production, so there is
 * nothing to edit here even if the page offered it.
 */
export const GotuAdminsTable = ({
  admins, isPending, isError, hasFilters, footer,
}) => {
  if (isError) {
    return (
      <TableCard title={null}>
        <Typography className="text-error">Could not load GoTu Admins.</Typography>
      </TableCard>
    );
  }

  return (
    <TableCard
      title={null}
      footer={footer}
      loading={isPending}
      empty={!isPending && admins.length === 0}
      emptyMessage={
        hasFilters ? 'No GoTu Admins match these filters.' : 'No GoTu Admins yet.'
      }
    >
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((admin) => (
              <TableRow key={admin.id} hover>
                <TableCell>{admin.name}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>{admin.roleLabel}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

GotuAdminsTable.propTypes = {
  admins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      roleLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /** Picks the empty copy: "nothing matched" reads as a bug with no filters set. */
  hasFilters: PropTypes.bool,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
};

GotuAdminsTable.defaultProps = { hasFilters: false, footer: null };

export default GotuAdminsTable;
